import { AdProviderTypes } from '@/types'

import { type AdLoader } from './ad-loader'
import { type InternalOutOfPageAdBanner } from './providers/internal-out-of-page-ad-banner'
import { BannerType, type BannerEventListenerArgs, type BannerEventType, type OutOfPageAdType } from './types'
import { AdConsoleLogger } from './utils/ad-console-logger'

const SHOW_TIMEOUT_AFTER_READY_MS = 2000

export enum PublicOutOfPageAdBannerState {
    'loading' = 'loading',
    'empty' = 'empty',
    'showing' = 'showing',
    'ready' = 'ready',
    'closed' = 'closed',
}

export class PublicOutOfPageAdBanner extends EventTarget {
    readonly type = BannerType.OUT_OF_PAGE

    readonly placement: OutOfPageAdType

    readonly id: string

    state: PublicOutOfPageAdBannerState = PublicOutOfPageAdBannerState.loading

    #intermediateInternalBanner: InternalOutOfPageAdBanner | undefined
    #internalBanner: InternalOutOfPageAdBanner | undefined
    #showTimeoutAfterReadyId: number | undefined

    #onDestroy: () => void

    readonly #loader: AdLoader | undefined

    readonly #adConsoleLogger: AdConsoleLogger

    constructor({
        placement,
        onDestroy,
        loader,
        timeout,
    }: {
        placement: OutOfPageAdType
        onDestroy: () => void
        loader?: AdLoader
        timeout: number
    }) {
        super()

        this.placement = placement
        this.id = crypto.randomUUID()
        this.#onDestroy = onDestroy
        this.#loader = loader
        window.setTimeout(() => {
            if (this.state === PublicOutOfPageAdBannerState.loading) {
                this.triggerEmpty('loader-timeout')
            }
        }, timeout)
        this.#loader?.show(placement)
        this.#adConsoleLogger = new AdConsoleLogger('public-banner', placement)
    }

    private dispatch<T>(event: BannerEventType, payload?: T) {
        this.dispatchEvent(new CustomEvent(event, { detail: payload }))
    }

    override addEventListener(...args: BannerEventListenerArgs) {
        super.addEventListener(...args)
    }

    override removeEventListener(...args: BannerEventListenerArgs) {
        super.removeEventListener(...args)
    }

    setIntermediateInternalBanner(banner: InternalOutOfPageAdBanner) {
        this.#intermediateInternalBanner = banner
    }

    setInternalBanner(banner: InternalOutOfPageAdBanner) {
        this.#intermediateInternalBanner = undefined
        this.#internalBanner = banner

        banner.addEventListener('showing', () => this.triggerShowing())
        banner.addEventListener('closed', (e) => this.triggerClosed(e.detail))
        banner.addEventListener('rewarded', (e) => this.triggerRewarded(e.detail))
        banner.addEventListener('viewable', () => this.triggerViewable())
        banner.addEventListener('timer', (e) => this.triggerTimer(e.detail?.secondsLeft as number))
        banner.addEventListener('ad_free', () => this.triggerAdFreeClicked())
        banner.addEventListener('pod_counter', (e) =>
            this.triggerPodCounter(e.detail?.adPosition as number, e.detail?.totalAds as number),
        )

        banner.addEventListener('interaction_suspected', () => {
            this.gracefullyDestroy('tab_left')
        })

        banner.addEventListener('clicked', () => {
            this.triggerClicked()
            if (banner.provider !== AdProviderTypes.APP) {
                this.gracefullyDestroy('clicked')
            }
        })
    }

    get adUnit() {
        return this.#internalBanner?.adUnit
    }

    get instreamDuration() {
        return this.#internalBanner?.instreamDuration
    }

    get watchedDuration() {
        return this.#internalBanner?.watchedDuration
    }

    get provider() {
        return this.#internalBanner?.provider
    }

    show() {
        this.clearShowTimeoutAfterReady()
        this.destroyLoader().then(() => {
            this.#internalBanner?.show()
        })
    }

    destroy() {
        this.clearShowTimeoutAfterReady()
        this.#onDestroy()

        this.#intermediateInternalBanner?.destroy()
        this.#intermediateInternalBanner = undefined

        this.#internalBanner?.destroy()
        this.#internalBanner = undefined
    }

    gracefullyDestroy(reason: string) {
        if (this.state === PublicOutOfPageAdBannerState.showing) {
            if (this.placement === 'rewarded') {
                this.triggerRewarded({})
            }
            this.triggerClosed({ reason })
        } else {
            this.triggerEmpty()
        }
    }

    setPlaybackState(isPlaying: boolean) {
        this.#internalBanner?.setPlaybackState(isPlaying)
    }

    triggerReady() {
        if (this.state !== PublicOutOfPageAdBannerState.ready) {
            this.#adConsoleLogger.debug(`${this.state} → ready`)
            this.state = PublicOutOfPageAdBannerState.ready
            this.startShowTimeoutAfterReady()
            this.dispatch('ready')
        }
    }

    triggerShowing() {
        if (this.state !== PublicOutOfPageAdBannerState.showing) {
            this.clearShowTimeoutAfterReady()
            this.#adConsoleLogger.debug(`${this.state} → showing`)
            this.state = PublicOutOfPageAdBannerState.showing
            this.dispatch('rendered')
        }
    }

    triggerViewable() {
        this.#adConsoleLogger.debug('viewable')
        this.dispatch('viewable')
    }

    triggerClicked() {
        this.dispatch('clicked')
    }

    triggerTimer(secondsLeft: number) {
        this.dispatch('timer', { secondsLeft })
    }

    triggerAdFreeClicked() {
        this.dispatch('ad_free', {})
    }

    triggerPodCounter(adPosition: number, totalAds: number) {
        this.dispatch('pod_counter', { adPosition, totalAds })
    }

    triggerRewarded(payload: unknown) {
        this.#adConsoleLogger.debug('rewarded')
        this.dispatch('rewarded', payload)
    }

    triggerClosed(payload?: unknown) {
        if (this.state !== PublicOutOfPageAdBannerState.closed) {
            this.#adConsoleLogger.debug(`${this.state} → closed`, payload)
            this.state = PublicOutOfPageAdBannerState.closed
            this.dispatch('closed', payload)
            this.destroy()
        }
    }

    triggerEmpty(reason?: 'timeout' | 'unfilled' | 'validation-error' | 'loader-timeout') {
        if (this.state !== PublicOutOfPageAdBannerState.empty) {
            this.#adConsoleLogger.debug(`${this.state} → empty`, { reason })
            this.state = PublicOutOfPageAdBannerState.empty
            this.destroyLoader().then(() => {
                this.dispatch('empty', { reason })
                this.destroy()
            })
        }
    }

    private startShowTimeoutAfterReady() {
        this.clearShowTimeoutAfterReady()
        this.#showTimeoutAfterReadyId = window.setTimeout(() => {
            this.#showTimeoutAfterReadyId = undefined
            if (this.state === PublicOutOfPageAdBannerState.ready) {
                this.dispatch('show_not_called_after_ready')
            }
        }, SHOW_TIMEOUT_AFTER_READY_MS)
    }

    private clearShowTimeoutAfterReady() {
        if (this.#showTimeoutAfterReadyId !== undefined) {
            window.clearTimeout(this.#showTimeoutAfterReadyId)
            this.#showTimeoutAfterReadyId = undefined
        }
    }

    private destroyLoader(): Promise<void> {
        return this.#loader ? this.#loader.destroy() : Promise.resolve()
    }
}
